import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../services/auth/auth.service';
import { MessageService } from 'primeng/api';
import { NavigationTrackerService } from '../services/navigation-tracker.service';

export interface PermissionRouteData {
  permissions?: string[]; // list of permission names
  match?: 'any' | 'all'; // default 'any'
  message?: string; // custom denial message
}

function hasRequiredPermissions(userPerms: string[], required: string[] = [], match: 'any' | 'all' = 'any'): boolean {
  if (!required.length) return true; // no restrictions
  if (!userPerms || !userPerms.length) return false;
  const set = new Set(userPerms);
  return match === 'all' ? required.every(p => set.has(p)) : required.some(p => set.has(p));
}

export const permissionsGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const auth = inject(AuthService);
  const msg = inject(MessageService);
  const nav: NavigationTrackerService = inject(NavigationTrackerService);

  const data = (route.data || {}) as PermissionRouteData;
  const userPerms = auth.getPermissions() || [];
  const allowed = hasRequiredPermissions(userPerms, data.permissions || [], data.match || 'any');

  if (allowed) return true;

  const info = data.message || 'You do not have permission to access this page.';
  try { msg.add({ severity: 'warn', summary: 'Access denied', detail: info, life: 4000 }); } catch {}

  const prev = nav.getPreviousUrl();
  const fallback = '/home';
  const target = prev && prev !== state.url ? prev : fallback;
  return router.parseUrl(target);
};
